/**
 * Tooltip
 *
 * Lightweight, instant (no-delay) tooltip that renders its bubble in a portal
 * positioned above the trigger element. Using a portal + fixed positioning
 * avoids clipping by table/row overflow containers, so it always floats nicely
 * on top of the icon.
 */

import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  /** Tooltip text/content shown on hover. */
  content: React.ReactNode;
  /** The trigger element the tooltip is anchored to. */
  children: React.ReactNode;
}

interface Coords {
  top: number;
  left: number;
}

export const Tooltip: React.FC<TooltipProps> = ({ content, children }) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  const show = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.top, left: rect.left + rect.width / 2 });
  }, []);

  const hide = useCallback(() => setCoords(null), []);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex"
      >
        {children}
      </span>

      {coords &&
        content != null &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: 'translate(-50%, -100%) translateY(-8px)',
              zIndex: 10000,
              pointerEvents: 'none',
            }}
            className="animate-[fadeIn_120ms_ease-out]"
          >
            <div className="relative rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg shadow-slate-900/30 whitespace-nowrap">
              {content}
              <span
                aria-hidden
                className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1 rotate-45 bg-slate-900"
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
